import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, LOCALE_ID, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownOption, UtcToLocalDateTimePipe } from '@cms/shared-library';
import { TranslateService } from '@ngx-translate/core';
import { ClassCourseWithEnrolmentsDTO } from 'src/app/models/class-coursewithenrolments-dto.model';
import { BulkCourseEnrolmentStatusUpdate, ClassEnrolment, EnrollmentFlowType, EnrolmentStatus, EnrolmentType } from 'src/app/models/class-enrolment.model';
import { CourseTrainingProgramDetails } from 'src/app/models/course-training-program-details.model';
import { LearnerDetail } from 'src/app/models/learner-detail.model';
import { TPWorker } from 'src/app/models/worker.model';
import { CourseService, uniqueEmailValidator } from 'src/app/services/course/course.service';
import { LookupObjectService } from 'src/app/services/lookup-object/lookup-object.service';
import { WorkerService } from 'src/app/services/worker-service/worker.service';
import { formatAddress } from 'src/app/utils/address.util';
import { FormatUtil } from 'src/app/utils/format.util';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { emailValidatorPattern, phoneValidatorPattern } from 'src/app/utils/validators.util';
import { EligibilityCheckerComponent } from '../eligibility-checker/eligibility-checker.component';
import { LearnerDetailComponent } from '../learner-detail/learner-detail.component';
import { UserService } from 'src/app/services/uppy/user.service';
import { bannerService } from 'src/app/services/banner-service/banner.service';
import { catchError, forkJoin, of } from 'rxjs';


@Component({
  selector: 'app-worker-enrollment',
  templateUrl: './worker-enrollment.component.html',
  styleUrls: ['./worker-enrollment.component.scss'],
  standalone: false
})
export class WorkerEnrollmentComponent implements OnInit {
  @ViewChildren('learnerDetails') lds: QueryList<LearnerDetailComponent>;
  @ViewChild('elig') eligChecker: EligibilityCheckerComponent;

  tpAppUtils = TrainingProgramApplicationUtils;
  formatUtil = FormatUtil

  datePipe: DatePipe
  utcDatePipe: UtcToLocalDateTimePipe = new UtcToLocalDateTimePipe()
  counterLabel: string = '';
  ENROLSTATUS = EnrolmentStatus;
  ENROLLMENT_FLOW_TYPE = EnrollmentFlowType;
  contactDetailsForm: FormGroup;
  learnerDetails: LearnerDetail[] = [];

  //Main data models
  courseDto: CourseTrainingProgramDetails
  courseWithEnrolments: ClassCourseWithEnrolmentsDTO;

  globalUpdateDate?: Date
  globalCreateDate?: Date

  currentLang: string = 'en';
  courseId: string = '';

  enrolmentDto: ClassEnrolment;
  primaryContact: ClassEnrolment = new ClassEnrolment();
  enrolments: ClassEnrolment[] = [];

  minNoOfSeats: number = 2
  noOfSeats: number = 2;
  maxNoOfSeats: number = 25;

  multipleEmailAddress: string = '';
  groupOrRegId: string;
  enrollmentFlowType: EnrollmentFlowType;

  isCreateMode: boolean = true;
  canUpdateFields = true
  isSingleTeamMember: boolean = false

  //Enrolment types
  enrolRequestOptions: DropdownOption[] = [new DropdownOption('Single enrolment', 'Inscription unique', '1'), new DropdownOption('Multiple enrolments', 'Inscriptions multiples', '2')];
  enrolOptions: DropdownOption[] = [new DropdownOption('My self', 'Moi-même', '1'), new DropdownOption('Team member', 'Membre de l’équipe', '2')];
  selectedEnrolRequestForOption: string = "1"
  singleOrMultiOption: string = "1"

  origContactEmail: string

  isTP: boolean = false
  isLearnerDetailEmpty: boolean = true;
  isCompleted: boolean = false
  isWorker: boolean = false
  WorkerId: string = "";
  worker: TPWorker;
  showCancel = false
  enrolmentGroupid: string;
  status: string
  potentialPrimaryLearnerId: string
  updatedPending = false;
  updatedConfirmed = false;
  confirmed = false;
  adminWorkOrMessageDirty = false;
  userRoles: string[];
  showCancelledEnrolmentConfirmedBanner: boolean;
  currentTpUserEmail: string = '';
  @ViewChildren('learnerDetail') learnerList: QueryList<LearnerDetailComponent>;

  constructor(
    @Inject('LookupObjectService') public lookupObjectService: LookupObjectService,
    @Inject('CourseService') public courseService: CourseService,
    @Inject('WorkerService') public workerService: WorkerService,
    @Inject('UserService') public userService: UserService,
    @Inject(LOCALE_ID) public locale: string,
    public translateService: TranslateService,
    public formBuilder: FormBuilder,
    public router: Router,
    public route: ActivatedRoute,
    private bannerService: bannerService,
    public cdr: ChangeDetectorRef) {
    translateService.onLangChange.subscribe(lang => {
      this.currentLang = lang.lang;
    });
    this.bannerService.currentBannerState.subscribe(state => this.showCancelledEnrolmentConfirmedBanner = state);
  }

  ngOnInit(): void {
    sessionStorage.removeItem('conflictingEmails')
    this.currentLang = this.translateService.currentLang;
    this.datePipe = new DatePipe(this.currentLang == 'en' ? 'en-CA' : 'fr-CA')

    if (this.route.snapshot.params['user']) {
      if (this.route.snapshot.params['user'] == "tp")
        this.isTP = true;
      else if (this.route.snapshot.params['user'] == "worker")
        this.isWorker = true;
    }

    forkJoin({
      userRoles: this.userService.getCurrentUserRoles().pipe(catchError(() => of([]))),
      user: this.userService.GetCurrentUser().pipe(catchError(() => of(null)))
    })
      .subscribe(data => {
        this.userRoles = data.userRoles ?? [];
        this.currentTpUserEmail = data.user?.EmailAddress ?? '';
        this.validateTpUserEmail();
        this.triggerLearnerEmailValidation();
      })

    if (this.isTP)
      this.canUpdateFields = false;
    if (this.route.snapshot.params['courseId']) {
      this.courseId = this.route.snapshot.params['courseId'];

      this.courseService.GetCourseDetails(this.courseId).subscribe((data) => {
        if (data != null) {
          this.courseDto = data;
          this.maxNoOfSeats = this.courseDto.Capacity ?? 25;
          this.groupOrRegId = this.route.snapshot.queryParams['GroupOrRegId'];
          this.status = this.route.snapshot.queryParams['Status']
          const enrolmentFlowType = this.route.snapshot.queryParams['EnrollmentFlowType'];
          if (enrolmentFlowType && !Number.isNaN(enrolmentFlowType)) {
            this.enrollmentFlowType = Number(enrolmentFlowType);
          }

          if (!this.groupOrRegId && !this.isWorker) this.buildContactForm(undefined, true)
          this.isSingleTeamMember = (this.singleOrMultiOption === '1' && this.selectedEnrolRequestForOption === '1')
          if (this.groupOrRegId) this.loadEnrolments();
          if (this.isWorker) this.loadWorkerDetails();
          this.courseDto.ClassTimes.sort((a, b) => {
            if ((a.StartDate ?? new Date()) < (b.StartDate ?? new Date())) return -1;
            if ((a.StartDate ?? new Date()) > (b.StartDate ?? new Date())) return 1;
            return 0;
          });
        }
      });
    }
  }

  private loadWorkerDetails(): void {
    this.workerService.GetCurrentWorker().subscribe(data => {
      this.WorkerId = data.WorkerDisplayId!;
      this.workerService.GetWorkerById(this.WorkerId).subscribe(data => {
        this.worker = data
        this.buildContactForm(
          {
            EmailAddress: this.worker.EmailAddress,
            FirstName: this.worker.FirstName,
            LastName: this.worker.LastName,
            Phone: this.worker.PhoneNumber,
            BirthYear: this.worker.BirthYear ? this.worker.BirthYear.toString() : ''
          },
          true);
        this.contactDetailsForm.markAsDirty()
      });
    });
  }

  private loadEnrolments(): void {
    this.courseService.GetEnrolmentDetailsByGroupIdOrRegistrationId(this.groupOrRegId).subscribe((resp) => {
      if (resp != null) {
        this.isCreateMode = false;
        this.courseWithEnrolments = resp;
        this.canUpdateFields = !this.isTP

        //if only one learner - option 1, else 2
        this.primaryContact = this.courseWithEnrolments.CourseEnrolments.length == 1 ? this.courseWithEnrolments.CourseEnrolments[0] : this.courseWithEnrolments.CourseEnrolments.filter(x => x.IsPrimary == true)[0]
        this.origContactEmail = this.primaryContact.EmailAddress
        this.buildContactForm(this.primaryContact, true)

        if (resp.CourseEnrolments[0].EnrolmentRequestType) this.setSingleOrMultiOption(resp.CourseEnrolments[0].EnrolmentRequestType!.toString());
        if (resp.CourseEnrolments[0].EnrolmentRequestFor) this.setEnrolRequestForOption(resp.CourseEnrolments[0].EnrolmentRequestFor!.toString())

        this.noOfSeats = (this.courseWithEnrolments.CourseEnrolments.length - 1)
        this.minNoOfSeats = this.noOfSeats
        let updateDates = this.courseWithEnrolments.CourseEnrolments.sort((a, b) => (a.LastUpdatedDate! > b.LastUpdatedDate!) ? -1 : 1)
        let createdDates = this.courseWithEnrolments.CourseEnrolments.sort((a, b) => (a.CreatedDate! > b.CreatedDate!) ? -1 : 1)
        this.globalUpdateDate = updateDates[0].LastUpdatedDate
        this.globalCreateDate = createdDates[0].LastUpdatedDate

        let potentialPrimaryLearner = this.courseWithEnrolments.CourseEnrolments.filter(x => x.IsPrimary != true && x.LearnerOrder == '1')
        if (potentialPrimaryLearner && potentialPrimaryLearner.length) this.potentialPrimaryLearnerId = potentialPrimaryLearner[0].RegistrationId!
        if (['pending', 'cancelled'].indexOf(this.status) > -1) this.courseWithEnrolments.CourseEnrolments = this.courseWithEnrolments.CourseEnrolments.filter(x => x.EnrolmentStatus == (this.status == 'pending' ? EnrolmentStatus.EnrolmentStatusPending : EnrolmentStatus.EnrolmentStatusCanceled))

        this.learnerDetails = this.courseWithEnrolments.CourseEnrolments.filter(x => x.IsPrimary != true).map(enrolment =>
          new LearnerDetail(enrolment.EmailAddress
            , undefined
            , undefined
            , enrolment.FirstName
            , enrolment.LastName
            , enrolment.Phone
            , enrolment.BirthYear != null ? parseInt(enrolment.BirthYear!) : undefined
            , enrolment.OptInForEmail
            , enrolment.OptInForText
            , enrolment.RegistrationId!
            , false
            , enrolment.IsPrimaryLearner,
            enrolment.NoteToProvider,
            enrolment.CourseFee,
            enrolment.AdminWorkCompleted,
            enrolment.Eligibility,
            enrolment.MessageForLearner,
            enrolment.IsPrimary,
            parseInt(enrolment.LearnerOrder!))).sort((a, b) => a.LearnerOrder! < b.LearnerOrder! ? -1 : a.LearnerOrder! > b.LearnerOrder! ? 1 : 0);

        if ((this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForLearner || this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForPrimaryContact || this.enrollmentFlowType === EnrollmentFlowType.EnrolledView) && this.learnerDetails) {
          this.learnerDetails[0].selected = true;
        }

        const learners = this.courseWithEnrolments.CourseEnrolments.filter(x => x.IsPrimary != true);
        if (learners && learners.length > 0) {
          this.isCompleted = learners[0].EnrolmentStatus == this.ENROLSTATUS.EnrolmentStatusCompleted;
          if (this.isCompleted) {
            this.translateService.get('ClassEnrolment.LearnerDetails').subscribe((translated: string) => { document.title = translated });
          }
        }
      }
    })
  }

  buildContactForm(primaryContact?: ClassEnrolment, setContactAsLearner: boolean = false) {
    if (!primaryContact) this.isLearnerDetailEmpty = true;
    if (primaryContact) this.isLearnerDetailEmpty = false;
    this.contactDetailsForm = this.formBuilder.group({
      emailAddress: new FormControl({ value: primaryContact ? primaryContact.EmailAddress : '', disabled: this.isWorker }, [Validators.pattern(emailValidatorPattern), Validators.required], [uniqueEmailValidator(this.courseService, this.courseId, primaryContact ? primaryContact.EmailAddress : undefined)]),
      firstName: new FormControl({ value: primaryContact ? primaryContact.FirstName : '', disabled: this.isWorker }, [Validators.required]),
      lastName: new FormControl({ value: primaryContact ? primaryContact.LastName : '', disabled: this.isWorker }, [Validators.required]),
      phone: new FormControl({ value: primaryContact ? primaryContact.Phone : '', disabled: this.isWorker }, [Validators.pattern(phoneValidatorPattern), Validators.required]),
      note: new FormControl(primaryContact ? primaryContact.NoteToProvider : '', []),
      registrationId: new FormControl(primaryContact ? primaryContact.RegistrationId : '', []),
      employeeName: new FormControl(primaryContact ? primaryContact.EmployeeName : '', []),
      updateOn: 'blur'
    })
    this.setContactAsLearner(setContactAsLearner, this.origContactEmail, primaryContact?.BirthYear)
  }

  getLabel() {
    if (this.selectedEnrolRequestForOption === '1') {
      this.translateService.get('ClassEnrolment.SeatsIncludingYourself').subscribe((translated: string) => {
        this.counterLabel = translated
      });
      return this.counterLabel
    } else if (this.selectedEnrolRequestForOption === '2') {
      this.translateService.get('ClassEnrolment.SeatsExcludingYourself').subscribe((translated: string) => {
        this.counterLabel = translated
      });
      return this.counterLabel
    } else {
      return '';
    }
  }

  setContactAsLearner(isLearner = false, contactEmail?: string, birthYear = '') {
    if (isLearner) {
      if (!this.contactDetailsForm.controls['birthYear']) this.contactDetailsForm.addControl('birthYear', new FormControl({ value: birthYear, disabled: this.isWorker }, []))
      if (!this.contactDetailsForm.controls['optInForEmail']) this.contactDetailsForm.addControl('optInForEmail', new FormControl(false, []))
      if (!this.contactDetailsForm.controls['optInForText']) this.contactDetailsForm.addControl('optInForText', new FormControl(false, []))

      this.contactDetailsForm.controls['emailAddress'].setAsyncValidators(uniqueEmailValidator(this.courseService, this.courseId, contactEmail))
      this.contactDetailsForm.controls['emailAddress'].updateValueAndValidity()
      if (this.contactDetailsForm.controls['employeeName'] && !this.contactDetailsForm.controls['employeeName'].value) this.contactDetailsForm.removeControl('employeeName')
    } else {
      this.contactDetailsForm.removeControl('birthYear')
      this.contactDetailsForm.removeControl('optInForEmail')
      this.contactDetailsForm.removeControl('optInForText')

      this.contactDetailsForm.controls['emailAddress'].clearAsyncValidators()
      this.contactDetailsForm.controls['emailAddress'].updateValueAndValidity()
      if (!this.contactDetailsForm.controls['employeeName']) this.contactDetailsForm.addControl('employeeName', new FormControl(''))
    }
  }

  saveEnrollment(status = 0): void {
    if (this.singleOrMultiOption === '1') {
      //my self
      if (this.selectedEnrolRequestForOption === '1') {
        this.saveSingleEnrollmentMyself(status)
      }
      else if (this.selectedEnrolRequestForOption === '2')
        this.saveSingleEnrollmentTeamMember(status)
    }
    else {
      this.saveMultipleEnrollmentMyselfAndTeamMembers();
    }
  }

  CheckIfLearnerInfoMatches() {
    this.courseService.CheckIfLearnerInfoMatches(this.enrolments).subscribe(i => {
      if (!i.infoMatches) {
        sessionStorage.setItem('conflictingEmails', JSON.stringify(i.conflictingEmails));
      }
    });
  }

  //Create/Update Single Enrollment with Pending or Confirmed status
  //Request Type: Single , Request For: Myself 
  //singleOrMultiOption === '1'
  //selectedEnrolRequestForOption === '1'
  //status = 0 - Pending
  //status = 1 - Confirm 
  //status = -1 - Cancelled
  saveSingleEnrollmentMyself(status): void {
    this.scrollToError();
    let routePrefix = this.currentLang == 'fr' ? 'fr/' : ''
    this.enrolments = [];
    if (!this.contactDetailsForm.valid) return;

    this.enrolmentDto = this.getEnrolmentDTOFromFormOrLearner(undefined, undefined, false, status);
    this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner(undefined, undefined, false, status));
    if (this.isWorker) {
      this.enrolmentDto.WorkerDisplayId = this.WorkerId;
    }

    //validate learner info first before enrolments submission to alert/prompt TPs (#139061)
    this.CheckIfLearnerInfoMatches();

    let sub = this.isTP ? this.courseService.SaveCourseEnrolments(this.enrolments)
      : this.courseService.SaveCourseEnrolmentsForPublic(this.enrolments);
    sub.subscribe(data => {
      if (this.isTP) {
        if (this.isCreateMode)
          this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: status, isTP: this.isTP, enrolmentCount: 1, self: true } });
        else {
          if (this.canUpdateFields) {
            this.status == 'pending' ? this.updatedPending = true : this.updatedConfirmed = true;
            this.canUpdateFields = false
          }
          else {
            if (status == this.ENROLSTATUS.EnrolmentStatusPending || status == this.ENROLSTATUS.EnrolmentStatusConfirmed)
              this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId],
                { queryParams: { tab: 'enrolment', status: status, isTP: this.isTP, enrolmentCount: 1, self: true, edit: true, update: "success" } });
            else if (status == this.ENROLSTATUS.EnrolmentStatusCanceled)
              this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: 'cancelled' } }),
                this.bannerService.changeBannerState(true);
          }
        }
      }
      else {
        sessionStorage.setItem('groupOrRegId', data);
        sessionStorage.setItem('enrolmentDetails', JSON.stringify(this.enrolments));
        sessionStorage.setItem('course', JSON.stringify(this.courseDto));

        this.isWorker ? this.router.navigate(['marketplace/enrolment-summary'], { queryParams: { isWorker: true } }) :
          this.router.navigate(['../../enrolment-summary'], { relativeTo: this.route });
      }
    });
  }

  //Create or Update Single Enrollment with Pending or Confirmed status
  //Request Type: Single , Request For: TeamMember 
  //singleOrMultiOption === '1'
  //selectedEnrolRequestForOption === '2'
  //status = 0 - Pending
  //status = 1 - Confirm 
  //status = -1 - cancel
  saveSingleEnrollmentTeamMember(status): void {
    this.scrollToError();
    let routePrefix = this.currentLang == 'fr' ? 'fr/' : ''
    this.enrolments = [];

    if (!this.contactDetailsForm.valid) return;
    if (this.lds != undefined && this.lds.first != undefined && !this.lds.first.newLearnerForm.valid) return

    //contact
    this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner(undefined, undefined, true, status));
    //learner
    this.learnerDetails[0].adminWorkCompleted = this.primaryContact.AdminWorkCompleted;
    this.learnerDetails[0].courseFee = this.primaryContact.CourseFee;
    this.learnerDetails[0].eligibility = this.primaryContact.Eligibility;
    this.learnerDetails[0].messageForLearner = this.primaryContact.MessageForLearner;
    this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner('1', this.learnerDetails[0], false, status));

    //validate learner info first before enrolments submission to alert/prompt TPs (#139061)
    this.CheckIfLearnerInfoMatches();

    let sub = this.isTP ? this.courseService.SaveCourseEnrolments(this.enrolments) : this.courseService.SaveCourseEnrolmentsForPublic(this.enrolments)
    sub.subscribe((data) => {
      if (this.isTP) {
        if (this.isCreateMode) this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: status, isTP: this.isTP, enrolmentCount: 1, self: false } });
        else {
          if (this.canUpdateFields) {
            this.status == 'pending' ? this.updatedPending = true : this.updatedConfirmed = true;
            this.canUpdateFields = false
          }
          else {
            if (status == this.ENROLSTATUS.EnrolmentStatusPending || status == this.ENROLSTATUS.EnrolmentStatusConfirmed)
              this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId],
                { queryParams: { tab: 'enrolment', status: status, isTP: this.isTP, enrolmentCount: 1, self: true, edit: true, update: "success" } });
            else if (status == this.ENROLSTATUS.EnrolmentStatusCanceled)
              this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: 'cancelled' } });
          }
        }
      }
      else {
        sessionStorage.setItem('groupOrRegId', data);
        sessionStorage.setItem('enrolmentDetails', JSON.stringify(this.enrolments));
        sessionStorage.setItem('course', JSON.stringify(this.courseDto));

        this.isWorker ? this.router.navigate(['marketplace/enrolment-summary'], { queryParams: { isWorker: true } }) :
          this.router.navigate(['../../enrolment-summary'], { relativeTo: this.route });
      }
    });

  }

  //Request Type: Multiple Enrolments , Request For: Myself & TeamMembers
  //singleOrMultiOption === '2'
  //selectedEnrolRequestForOption === '1'
  //Request Type: Multiple Enrolments , Request For: TeamMembers
  //singleOrMultiOption === '2'
  //selectedEnrolRequestForOption === '2'
  //status = 0 - Pending
  //status = 1 - Confirm 
  saveMultipleEnrollmentMyselfAndTeamMembers(status = 0) {
    this.scrollToError();
    let routePrefix = this.currentLang == 'fr' ? 'fr/' : ''
    this.enrolments = [];
    this.learnerList.forEach(ld => ld.markAllControlsAsTouched());

    if (!this.contactDetailsForm.valid) return;

    this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner(undefined, undefined, true, status));
    this.multipleEmailAddress = this.contactDetailsForm!.controls['emailAddress'].value;

    //primary contact & learner
    if (this.selectedEnrolRequestForOption === '1' && this.lds.first && !this.lds.first.newLearnerForm.valid) return

    let learnerCnt = 0;
    this.learnerDetails.forEach(learner => {
      if (learner.emailAddress && learner.emailAddress != '') this.multipleEmailAddress += ', ' + learner.emailAddress
      this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner((learnerCnt + 1).toString(), learner, false, status))
      learnerCnt += 1;
    });

    if (status == this.ENROLSTATUS.EnrolmentStatusConfirmed) {
      for (let i = 0; i < this.enrolments.length; i++) {
        this.enrolments[i].MessageForLearner = this.primaryContact.MessageForLearner;
      }
    }

    //validate learner info first before enrolments submission to alert/prompt TPs (#139061)
    this.CheckIfLearnerInfoMatches();

    let sub = this.isTP ? this.courseService.SaveCourseEnrolments(this.enrolments) : this.courseService.SaveCourseEnrolmentsForPublic(this.enrolments)
    sub.subscribe(data => {
      if (this.isTP) {
        this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: status, isTP: this.isTP, enrolmentCount: learnerCnt } });
      }
      else {
        sessionStorage.setItem('groupOrRegId', data);
        sessionStorage.setItem('enrolmentDetails', JSON.stringify(this.enrolments));
        sessionStorage.setItem('course', JSON.stringify(this.courseDto));

        this.isWorker ? this.router.navigate(['marketplace/enrolment-summary'], { queryParams: { isWorker: true } }) :
          this.router.navigate(['../../enrolment-summary'], { relativeTo: this.route });
      }
    });

  }

  updateMultipleEnrollmentMyselfAndTeamMembers(status = 0, onlySelectedLearners: boolean = false) {
    if (this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForLearner || this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForPrimaryContact) {
      this.saveConfirmedEditedEnrollments();
    }
    this.scrollToError();
    let routePrefix = this.currentLang == 'fr' ? 'fr/' : ''
    this.enrolments = [];
    if (status !== this.ENROLSTATUS.EnrolmentStatusPending || !onlySelectedLearners) {
      // skip form validation for "Move to Pending" action
      if (!this.contactDetailsForm.valid) return;
    }
    if (!onlySelectedLearners) this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner(undefined, undefined, true, status));
    this.multipleEmailAddress = this.contactDetailsForm!.controls['emailAddress'].value;
    if (this.selectedEnrolRequestForOption === '1' && this.lds.first && !this.lds.first.newLearnerForm.valid) return
    let learnerCnt = 0;
    this.learnerDetails.forEach(learner => {
      if (learner.emailAddress && learner.emailAddress != '') this.multipleEmailAddress += ', ' + learner.emailAddress
      if ((onlySelectedLearners && learner.selected) || this.canUpdateFields) {
        this.enrolments.push(this.getEnrolmentDTOFromFormOrLearner((learnerCnt + 1).toString(), learner, false, status))
        learnerCnt += 1;
      }
    });
    if (status == this.ENROLSTATUS.EnrolmentStatusConfirmed) {
      for (let i = 0; i < this.enrolments.length; i++) {
        this.enrolments[i].MessageForLearner = this.primaryContact.MessageForLearner;
      }
    }
    let sub = this.courseService.SaveCourseEnrolments(this.enrolments)
    sub.subscribe(data => {
      if (this.canUpdateFields) {
        this.status == 'pending' ? this.updatedPending = true : this.updatedConfirmed = true;
        this.canUpdateFields = false;
        this.loadEnrolments();
      }
      else {
        if (status == this.ENROLSTATUS.EnrolmentStatusConfirmed)
          this.router.navigate(['training-provider/view-classroom', this.courseId], { queryParams: { tab: 'enrolment', edit: true, status: this.ENROLSTATUS.EnrolmentStatusConfirmed, isTP: this.isTP } });
        else if (status == this.ENROLSTATUS.EnrolmentStatusCanceled)
          this.router.navigate([routePrefix + 'training-provider/view-classroom/' + this.courseId], { queryParams: { tab: 'enrolment', status: 'cancelled' } });
        else if (status == this.ENROLSTATUS.EnrolmentStatusPending)
          this.router.navigate(['training-provider/view-classroom', this.courseId], { queryParams: { tab: 'enrolment', edit: true, status: 'pending' } });
      }
    });
  }

  setEnrolRequestForOption(id: string) {
    if (this.selectedEnrolRequestForOption != id) {
      this.selectedEnrolRequestForOption = id

      this.setContactAsLearner(this.singleOrMultiOption == '1' && id === '1', this.origContactEmail)

      //single enrol
      if (this.singleOrMultiOption === '1') {
        this.noOfSeats = 2;

        //add a learner if for team member but no learner exists - otherwise clear learner details
        if (id == '2') this.learnerDetails = !this.learnerDetails || this.learnerDetails.length == 0 ? [new LearnerDetail('')] : this.learnerDetails
        else this.learnerDetails = []
      }
      //multi enrol
      else if (this.singleOrMultiOption === '2') {
        this.noOfSeats = Math.max(2, this.noOfSeats);
        for (let i = this.learnerDetails.length; i < 2; i++) this.learnerDetails.push(new LearnerDetail(''));
        if (this.selectedEnrolRequestForOption == '1') {
          this.learnerDetails[0].isPrimaryLearner = true;
        } else {
          this.learnerDetails[0].isPrimaryLearner = false;
        }
      }
      this.triggerLearnerEmailValidation();
    }
  }

  setSingleOrMultiOption(id: string) {
    if (this.singleOrMultiOption != id) {

      this.singleOrMultiOption = id;
      this.selectedEnrolRequestForOption = '';
      this.setEnrolRequestForOption("1")

      if (id === '1') {
        this.enrolOptions[0].name = 'My self';
        this.enrolOptions[0].nameFr = 'Moi-même';
        this.enrolOptions[1].name = 'Team member';
        this.enrolOptions[1].nameFr = 'Membre de l’équipe ';
      }
      else if (id === '2') {
        this.enrolOptions[0].name = 'My self & team members';
        this.enrolOptions[0].nameFr = 'Moi-même et les membres de l’équipe ';
        this.enrolOptions[1].name = 'Team members';
        this.enrolOptions[1].nameFr = 'Membres de l’équipe ';
      }

      this.triggerLearnerEmailValidation();
    }
  }

  private triggerLearnerEmailValidation(attempt = 0) {
    this.cdr.detectChanges();

    setTimeout(() => {
      const expectedLearnerCount = this.learnerDetails?.length ?? 0;
      if (expectedLearnerCount > 0 && (!this.lds || this.lds.length < expectedLearnerCount) && attempt < 5) {
        this.triggerLearnerEmailValidation(attempt + 1);
        return;
      }

      if (!this.lds || this.lds.length === 0) return;

      const hasUninitializedEmailControls = this.lds.some(ld => !ld?.newLearnerForm?.controls?.['emailAddress']);
      if (hasUninitializedEmailControls && attempt < 5) {
        this.triggerLearnerEmailValidation(attempt + 1);
        return;
      }

      this.lds.forEach((ld, i) => {
        const emailControl = ld?.newLearnerForm?.controls?.['emailAddress'];
        if (!emailControl) return;

        emailControl.markAsTouched();
        emailControl.markAsDirty();
        this.validateLearnerEmail(i);
      });
    });
  }

  CreateSeats(seatNumber: number, isFromNumeric = false) {
    if (this.learnerDetails[seatNumber - 1]) this.learnerDetails.splice(this.noOfSeats, this.learnerDetails.length);
    else {
      while (this.learnerDetails.length <= (seatNumber - 1)) {
        let ld = new LearnerDetail('');
        this.learnerDetails.push(ld);
      }
      if (!isFromNumeric) this.noOfSeats++
      this.cdr.detectChanges()

      if (this.lds && this.lds.length > 0) {
        this.lds.last.expanded = true
      }
    }
  }

  DeleteLearner(index: number) {
    this.learnerDetails.splice(index, 1);
    this.noOfSeats--;
  }

  PastePrimaryContactInfoInChildComponent() {
    this.lds.first.newLearnerForm.controls['emailAddress'].setValue(this.contactDetailsForm!.controls['emailAddress'].value);
    this.lds.first.newLearnerForm.controls['firstName'].setValue(this.contactDetailsForm!.controls['firstName'].value);
    this.lds.first.newLearnerForm.controls['lastName'].setValue(this.contactDetailsForm!.controls['lastName'].value);
    this.lds.first.newLearnerForm.controls['phone'].setValue(this.contactDetailsForm!.controls['phone'].value);
    this.lds.first.inforSameAsPC = true;
    this.lds.first.newLearnerForm.updateValueAndValidity()
  }

  UnCheckCheckBoxInChild() {
    if (this.lds && this.lds.first) this.lds.first.ClearCheckBox();
  }

  DisableSubmitButton() {
    //single
    if (this.singleOrMultiOption === '1') {
      //only 1 contact
      if (this.selectedEnrolRequestForOption === '1' && this.contactDetailsForm.valid && this.contactDetailsForm.dirty) return false;
      //team member
      else if (this.selectedEnrolRequestForOption === '2' && this.contactDetailsForm.valid
        && this.lds.first && this.lds.first.newLearnerForm.valid && (this.lds.first.newLearnerForm.dirty || this.contactDetailsForm.dirty)) return false;
      else return true;
    }
    //multiple
    else if (this.singleOrMultiOption === '2') {
      if (this.contactDetailsForm && this.contactDetailsForm.valid && !this.lds?.find(a => !a.newLearnerForm.valid) && (this.contactDetailsForm.dirty || this.lds?.find(a => a.newLearnerForm.dirty) || this.adminWorkOrMessageDirty)) return false
      else return true
    }
    else return true;
  }

  getEnrolmentRequestType() {
    let enrolmentReqOption = this.enrolRequestOptions.find(a => a.value === this.singleOrMultiOption);
    return (this.currentLang === 'en' ? enrolmentReqOption?.name : enrolmentReqOption?.nameFr)
  }

  getEnrolmentRequestFor() {
    let enrolmentOption = this.enrolOptions.find(a => a.value === this.selectedEnrolRequestForOption);
    return (this.currentLang === 'en' ? enrolmentOption?.name : enrolmentOption?.nameFr)
  }

  validateTpUserEmail() { //case when TP user is trying to use own email address for learner
    if (!this.contactDetailsForm?.controls?.['emailAddress']) return;

    let email = this.contactDetailsForm.controls['emailAddress']
    if (email.valid && email.value) {
      //only validate the TP user is logged in, and when the contact form is used for learner details (single enrol request for myself)
      if (email.value.toLowerCase() == this.currentTpUserEmail.toLowerCase()
        && (this.isTP && this.singleOrMultiOption === '1' && this.selectedEnrolRequestForOption === '1')
      ) email.setErrors({ currentTpUser: true })
      else if (email && email.errors && email.errors['currentTpUser']) delete email.errors['currentTpUser']
    }
  }

  validateLearnerEmail(ldIndex: number) {
    let learnerDetailComponent = this.lds.get(ldIndex)
    let email = learnerDetailComponent?.newLearnerForm.controls['emailAddress']!
    if (email.valid && email.value) {
      let emailList = this.lds.map(x => x.newLearnerForm.controls['emailAddress'].value)
      if (emailList.filter(x => x == email.value).length > 1) email?.setErrors({ exists: true })
      else if (email && email.errors && email.errors['exists']) delete email.errors['exists']

      if (email.value.toLowerCase() == this.currentTpUserEmail.toLowerCase() && this.isTP) email.setErrors({ currentTpUser: true })
      else if (email && email.errors && email.errors['currentTpUser']) delete email.errors['currentTpUser']
    }
  }

  cancelSelected() {
    let request = new BulkCourseEnrolmentStatusUpdate;
    request.GroupOrRegistrationIds = this.learnerDetails.filter(x => x.selected).map(x => x.registrationId!);
    request.EnrolmentStatus = EnrolmentStatus.EnrolmentStatusCanceled;
    request.EnrolmentType = EnrolmentType.Worker;
    this.courseService
      .UpdateEnrolmentDetailsInBulkByGroupIdOrRegistrationId(request)
      .subscribe((success) => {
        if (success)
          this.backToCourseSearch(true);
      });

  }

  formatAddress() { return formatAddress(this.courseDto) }

  getDate(date, format) {
    return this.datePipe.transform(date, format)
  }

  getTime(date) {
    let curDate = new Date(date.toString() + 'Z')

    let hour = this.datePipe.transform(curDate, 'h')
    let minutes = this.datePipe.transform(curDate, 'mm')
    let ampm = this.datePipe.transform(curDate, 'a')

    let time = hour
    if (minutes != '00') time += (':' + minutes)
    time += ' ' + ampm!.toLocaleLowerCase()
    return time
  }

  backToMarketplace() {
    const langRoute = this.currentLang === 'en' ? 'marketplace' : 'fr/marketplace';
    this.router.navigate([`${langRoute}/course-search`]);
  }

  backToCourseSearch(fromCancel: boolean = false) {
    if (this.groupOrRegId || this.isTP) {
      const langRoute = this.currentLang === 'en' ? 'training-provider/view-classroom' : 'fr/training-provider/view-classroom';
      if (fromCancel)
        this.router.navigate([langRoute, this.courseId], { queryParams: { tab: 'enrolment', status: 'cancelled', program: this.learnerDetails.filter(x => x.selected).length.toString() } });
      else
        this.router.navigate([langRoute, this.courseId], { queryParams: { tab: 'enrolment', status: this.status } });
    }
    else {
      if (this.isWorker) {
        const langRoute = 'worker/training-schedules';
        this.router.navigate([`${langRoute}/course-search`]);
      }
      else {
        const langRoute = this.currentLang === 'en' ? 'safety-and-prevention' : 'fr/safety-and-prevention';
        this.router.navigate([`${langRoute}/cpo-approved-courses`]);
      }
    }
  }

  private getEnrolmentDTOFromFormOrLearner(learnerOrder?: string, learner?: LearnerDetail, IsPrimary?: boolean, status?: EnrolmentStatus): ClassEnrolment {
    let eligibility: boolean | undefined
    let sameAsPrimary: boolean = false;
    //for eligibility, if are the TP
    if (this.isTP) eligibility = learner == null ? this.primaryContact.Eligibility : learner.eligibility
    //public user
    else {
      let eligChecker: EligibilityCheckerComponent | undefined = this.eligChecker
      if (learner && learnerOrder) eligChecker = this.lds.get(parseInt(learnerOrder ?? '1') - 1)?.eligChecker

      if (eligChecker == undefined || eligChecker?.bannerType == '') eligibility = undefined
      else eligibility = eligChecker && (!eligChecker.shareEligibilityResults || (eligChecker.bannerType && eligChecker.bannerType == 'userNotFound')) ? undefined : (eligChecker.bannerType === 'success' ? true : false)
    }

    if (learner && learnerOrder) sameAsPrimary = this.lds.get(parseInt(learnerOrder ?? '1') - 1)?.inforSameAsPC ?? learner.isPrimaryLearner ?? false;
    else if (learner) sameAsPrimary = learner.isPrimaryLearner ?? false;

    return {
      RegistrationId: learner == null ? (this.contactDetailsForm.controls["registrationId"].value == '' ? null : this.contactDetailsForm.controls["registrationId"].value) : learner?.registrationId,
      CourseId: this.courseId,
      EmailAddress: learner == null ? this.contactDetailsForm.controls["emailAddress"].value : learner.emailAddress,
      Phone: learner == null ? this.contactDetailsForm.controls["phone"].value : FormatUtil.replaceUndefinedWithEmpty(learner.phoneNumber),
      FirstName: learner == null ? this.contactDetailsForm.controls["firstName"].value : FormatUtil.replaceUndefinedWithEmpty(learner.firstName),
      LastName: learner == null ? this.contactDetailsForm.controls["lastName"].value : FormatUtil.replaceUndefinedWithEmpty(learner.lastName),
      BirthYear: learner == null ? !this.contactDetailsForm.controls["birthYear"] || this.contactDetailsForm.controls["birthYear"].value === '' ? null : this.contactDetailsForm.controls["birthYear"].value : (learner.birthYear ? learner.birthYear?.toString() : undefined),
      NoteToProvider: !this.contactDetailsForm.controls["note"] || this.contactDetailsForm.controls["note"].value === '' ? null : this.contactDetailsForm.controls["note"].value,
      OptInForEmail: learner == null ? this.contactDetailsForm.controls["optInForEmail"] ? this.contactDetailsForm.controls["optInForEmail"].value : null : learner.optinforEmail,
      OptInForText: learner == null ? this.contactDetailsForm.controls["optInForText"] ? this.contactDetailsForm.controls["optInForText"].value : null : learner.optinforSMS,
      EmployeeName: !this.contactDetailsForm.controls["employeeName"] || this.contactDetailsForm.controls["employeeName"].value === '' ? null : this.contactDetailsForm.controls["employeeName"].value,
      EnrolmentRequestType: this.singleOrMultiOption,
      EnrolmentRequestFor: this.selectedEnrolRequestForOption,
      EnrolmentStatus: status ?? EnrolmentStatus.EnrolmentStatusPending,
      EnrolmentType: this.isTP ? EnrolmentType.TrainingProvider : EnrolmentType.Worker,
      CreatedDate: new Date(),
      ProgramName: this.courseDto.ProgramFullName,
      ProgramNameFr: this.courseDto.ProgramFullNameFr,
      TrainingProviderName: this.courseDto.TrainingProviderName,
      LearnerOrder: learnerOrder,
      EnrolmentGroupId: this.groupOrRegId,
      IsPrimary: learner == null ? IsPrimary : false,
      IsPrimaryLearner: sameAsPrimary,
      Eligibility: eligibility,
      MessageForLearner: learner == null ? this.primaryContact.MessageForLearner : learner.messageForLearner,
      CourseFee: this.primaryContact.CourseFee,
      AdminWorkCompleted: this.primaryContact.AdminWorkCompleted,
    }
  }

  private scrollToError() {
    this.contactDetailsForm.markAllAsTouched();
    this.cdr.detectChanges()

    if (!this.lds?.find(a => !a.newLearnerForm.valid) && this.contactDetailsForm.valid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else {

      let elem = document.getElementsByTagName('mat-error')[0] as HTMLElement
      if (elem) {
        var rect = elem.getBoundingClientRect();

        let isVisible = (rect.top - 100) >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth)

        if (!isVisible) {
          elem.scrollIntoView()
          window.scrollBy(0, -100);
        }
      }
    }
  }

  selectLearner(learnerId) {
    let learner = this.learnerDetails.find(x => x.registrationId == learnerId);
    if (learner) {
      learner.selected = !learner.selected
      this.showCancel = this.learnerDetails.filter(x => x.selected).length > 0;
    }
  }

  setEnrolmentGroupId(enrolmentGroupid) {
    this.enrolmentGroupid = enrolmentGroupid;
  }

  hasLearnerWithStatus(status) {
    return this.courseWithEnrolments && this.courseWithEnrolments.CourseEnrolments.filter(x => x.EnrolmentStatus == status).length > 0;
  }

  isLearnerInfoRequired(index: number) {
    if (this.singleOrMultiOption == '1' && this.selectedEnrolRequestForOption == '2') {
      return false;
    }
    else if (this.isCreateMode) {
      return ((this.selectedEnrolRequestForOption === '1' || this.singleOrMultiOption == '1') && index == 0)
    }
    else {
      return (this.potentialPrimaryLearnerId && this.selectedEnrolRequestForOption === '1' && this.learnerDetails[index].registrationId === this.potentialPrimaryLearnerId) || this.singleOrMultiOption == '1'
    }
  }

  isSubscriptionVisible() {
    if (this.isCompleted) return false;
    else if (this.singleOrMultiOption == '1' && (this.selectedEnrolRequestForOption == '1' || this.selectedEnrolRequestForOption == '2')) {
      return true;
    } else {
      return !(this.isCreateMode || this.canUpdateFields) && this.isTP && this.singleOrMultiOption == '2';
    }
  }


  editEnrollment() {
    //check team member
    if (this.singleOrMultiOption === '1' && this.selectedEnrolRequestForOption === '2') {
      this.courseService.GetEnrolmentDetailsByGroupIdOrRegistrationId(this.enrolmentGroupid).subscribe((enrolmentDetails) => {
        for (const CourseEnrolment of enrolmentDetails.CourseEnrolments) {
          if (CourseEnrolment.IsPrimary) {
            this.primaryContact = CourseEnrolment;
            this.buildContactForm(this.primaryContact, false);
          }
        }
        this.enrollmentFlowType = this.ENROLLMENT_FLOW_TYPE.EnrolledEdit;
        this.canUpdateFields = true;
        this.cdr.detectChanges();
      });
    } else if (this.singleOrMultiOption === '2') {
      this.courseService
        .GetEnrolmentDetailsByGroupIdOrRegistrationId(this.enrolmentGroupid)
        .subscribe((enrolmentDetails) => {
          const primaryContactOrLearnerBasedOnRegistrationId = enrolmentDetails.CourseEnrolments.find(x => x.RegistrationId === this.groupOrRegId);
          if (primaryContactOrLearnerBasedOnRegistrationId) {
            if (primaryContactOrLearnerBasedOnRegistrationId.IsPrimary) {
              this.enrollmentFlowType = this.ENROLLMENT_FLOW_TYPE.EnrolledEditForPrimaryContact;
              this.setContactAsLearner(false, this.origContactEmail);
              if (this.contactDetailsForm.controls['employeeName'])
                this.contactDetailsForm.removeControl('employeeName')
              if (this.contactDetailsForm.controls['note'])
                this.contactDetailsForm.removeControl('note')
              this.contactDetailsForm.addControl('employeeName', new FormControl(primaryContactOrLearnerBasedOnRegistrationId.EmployeeName));
              this.contactDetailsForm.addControl('note', new FormControl(primaryContactOrLearnerBasedOnRegistrationId.NoteToProvider));
              if (this.contactDetailsForm.controls['birthYear']) this.contactDetailsForm.removeControl('birthYear');
            }
            else {
              this.enrollmentFlowType = this.ENROLLMENT_FLOW_TYPE.EnrolledEditForLearner;
              this.setContactAsLearner(true, this.origContactEmail);
              this.contactDetailsForm.removeControl('employeeName');
              this.contactDetailsForm.removeControl('note');
              if (!this.contactDetailsForm.controls['birthYear']) this.contactDetailsForm.addControl('birthYear', new FormControl(primaryContactOrLearnerBasedOnRegistrationId.BirthYear));
              else this.contactDetailsForm.controls['birthYear'].setValue(primaryContactOrLearnerBasedOnRegistrationId.BirthYear);
            }
          }
          this.canUpdateFields = true;
          this.cdr.detectChanges();
        });
    } else {
      this.enrollmentFlowType = this.ENROLLMENT_FLOW_TYPE.EnrolledEdit;
      this.canUpdateFields = true;
      this.cdr.detectChanges();
    }
  }

  saveConfirmedEditedEnrollments() {
    if (this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForLearner) {
      this.learnerDetails[0].emailAddress = this.contactDetailsForm.controls["emailAddress"].value;
      this.learnerDetails[0].phoneNumber = this.contactDetailsForm.controls["phone"].value;
      this.learnerDetails[0].firstName = this.contactDetailsForm.controls["firstName"].value;
      this.learnerDetails[0].lastName = this.contactDetailsForm.controls["lastName"].value;
      this.learnerDetails[0].birthYear = !this.contactDetailsForm.controls["birthYear"] || this.contactDetailsForm.controls["birthYear"].value === '' ? null : this.contactDetailsForm.controls["birthYear"].value;
    }
  }

  cancelEditing(): void {
    if (this.contactDetailsForm.dirty || this.lds?.find(a => a.newLearnerForm.dirty)) {
      this.ngOnInit();
    }
    else {
      this.canUpdateFields = !this.canUpdateFields
    }

    this.resetEnrollmentFlowType();
  }

  private resetEnrollmentFlowType() {
    if (this.enrollmentFlowType === EnrollmentFlowType.EnrolledEdit || this.enrollmentFlowType === EnrollmentFlowType.EnrolledEditForLearner) {
      const enrolmentFlowType = this.route.snapshot.queryParams['EnrollmentFlowType'];
      if (enrolmentFlowType && !Number.isNaN(enrolmentFlowType)) {
        if (Number(enrolmentFlowType) == EnrollmentFlowType.EnrolledView) {
          this.enrollmentFlowType = EnrollmentFlowType.EnrolledView;
        }
      }
    }
  }

  adminWorkOrMessageChanged(val: any) {
    this.adminWorkOrMessageDirty = true;
  }

  NoSelectedLearners() {
    return this.learnerDetails.filter(x => x.selected).length <= 0;
  }
  confirmEnrolmentDisabled(): boolean {
    if (this.singleOrMultiOption == '1' && this.enrollmentFlowType == this.ENROLLMENT_FLOW_TYPE.EnrolledView && this.isCreateMode) {
      return (this.canUpdateFields || this.isCreateMode) ? this.DisableSubmitButton() : false;
    }
    else return !((this.singleOrMultiOption == '1') && this.courseWithEnrolments != null && this.hasLearnerWithStatus(this.ENROLSTATUS.EnrolmentStatusPending));
  }

  updateMySelf() {
    if (this.learnerDetails[0]?.isPrimaryLearner)
      this.PastePrimaryContactInfoInChildComponent();
  }

  canAccess(): boolean {
    if (!this.userRoles || this.userRoles.length < 1) {
      return false;
    }
    for (let roleId of this.userRoles) {
      roleId = roleId.toLowerCase();

      if (roleId == TrainingProgramApplicationUtils.AdministratorId.toLowerCase() ||
        roleId == TrainingProgramApplicationUtils.DesignatedSigningAuthorityId.toLowerCase() ||
        roleId == TrainingProgramApplicationUtils.AffiliateAdministrator.toLowerCase()) {
        return true;
      }
    }
    return false;
  }
}

